'use client'
import { useCallback, useEffect, useRef, useState } from 'react'

import GlassisTeaserGlassesSvg from './glassis-teaser-glasses-svg'
import { TouchHereArrowSvg } from './touch-here-arrow-svg'
import { TouchHereSvg } from './touch-here-svg'
import { ArrowRightSvg } from './arrow-right-svg'
import { ButtonTextSvg } from './button-text-svg'
import { TeasingTextSvg } from './teasing-text-svg'
import { GlassisForm } from './glassis-form'

import FlexCol from '@/components/shared/FlexCol'
import { amplitudeTracker } from '@/utils/amplitude'
import { isCurrentTimeInRange } from '@/utils/date'
import { GLASSIS_TEASER_END_TIME } from '@/features/promotion/constants'

const HOLE_CONFIG = {
  // 자동 원(배경 노출 원) 관련
  auto: {
    appearDelay: 2000, // 방문 후 원이 나타나는 시간(ms)
    minSize: 60, // px
    maxSize: 80, // px
    pulseDuration: 1000, // ms, 1회 커졌다 줄었다 주기
    position: { x: 0.5, y: 0.3 }, // 화면 비율(가운데 살짝 위)
  },
  // 터치 원 관련
  touch: {
    initialSize: 100, // px
    maxSize: 200, // px
    growDuration: 1000, // ms, 1초간 커짐
  },
}

interface TouchPosition {
  x: number
  y: number
}

const GlassisTeaserPage = () => {
  const [touchPosition, setTouchPosition] = useState<TouchPosition | null>(null)
  const [touchHoleSize, setTouchHoleSize] = useState(0)
  const [isTouching, setIsTouching] = useState(false)
  const [isTouchHoleVisible, setIsTouchHoleVisible] = useState(false)
  const touchGrowAnim = useRef<number | null>(null)

  // 자동 원 관련 상태
  const [isAutoHoleVisible, setIsAutoHoleVisible] = useState(false)
  const [autoHoleSize, setAutoHoleSize] = useState(HOLE_CONFIG.auto.minSize)
  const autoPulseAnim = useRef<number | null>(null)
  const autoAppearTimeout = useRef<NodeJS.Timeout | null>(null)

  // 방문 후 자동 원 타이머 시작
  useEffect(() => {
    if (isTouchHoleVisible) return
    autoAppearTimeout.current = setTimeout(() => {
      setIsAutoHoleVisible(true)
    }, HOLE_CONFIG.auto.appearDelay)
    return () => {
      if (autoAppearTimeout.current) clearTimeout(autoAppearTimeout.current)
    }
  }, [isTouchHoleVisible])

  // 자동 원 pulse 애니메이션
  useEffect(() => {
    if (!isAutoHoleVisible) return
    const min = HOLE_CONFIG.auto.minSize
    const max = HOLE_CONFIG.auto.maxSize
    const duration = HOLE_CONFIG.auto.pulseDuration
    const start = performance.now()
    function pulse(now: number) {
      const elapsed = (now - start) % duration
      const t = elapsed / duration
      const s = Math.sin(Math.PI * t)
      const size = min + (max - min) * s
      setAutoHoleSize(size)
      autoPulseAnim.current = requestAnimationFrame(pulse)
    }
    autoPulseAnim.current = requestAnimationFrame(pulse)
    return () => {
      if (autoPulseAnim.current) cancelAnimationFrame(autoPulseAnim.current)
    }
  }, [isAutoHoleVisible])

  // 터치/마우스 시작
  const handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (autoAppearTimeout.current) clearTimeout(autoAppearTimeout.current)
    setIsAutoHoleVisible(false)
    if (autoPulseAnim.current) cancelAnimationFrame(autoPulseAnim.current)
    if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    setTouchPosition({ x: clientX, y: clientY })
    setIsTouchHoleVisible(true)
    setTouchHoleSize(HOLE_CONFIG.touch.initialSize)
    setIsTouching(true)
  }, [])

  // grow 애니메이션 useEffect
  useEffect(() => {
    if (!isTouching) return
    if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    const startTime = performance.now()
    const startSize = HOLE_CONFIG.touch.initialSize
    const endSize = HOLE_CONFIG.touch.maxSize
    const duration = HOLE_CONFIG.touch.growDuration
    function grow(now: number) {
      const elapsed = now - startTime
      const t = Math.min(elapsed / duration, 1)
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      const size = startSize + (endSize - startSize) * ease
      setTouchHoleSize(size)
      if (t < 1 && isTouching) {
        touchGrowAnim.current = requestAnimationFrame(grow)
      } else {
        setTouchHoleSize(endSize)
      }
    }
    touchGrowAnim.current = requestAnimationFrame(grow)
    return () => {
      if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    }
  }, [isTouching])

  // shrink 애니메이션 useEffect
  useEffect(() => {
    if (isTouching || !isTouchHoleVisible) return
    if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    const startSize = touchHoleSize
    const endSize = 0
    const duration = 300
    const startTime = performance.now()
    function shrink(now: number) {
      const elapsed = now - startTime
      const t = Math.min(elapsed / duration, 1)
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      const size = startSize + (endSize - startSize) * ease
      setTouchHoleSize(size)
      if (t < 1) {
        touchGrowAnim.current = requestAnimationFrame(shrink)
      } else {
        setIsTouchHoleVisible(false)
        setTouchHoleSize(0)
      }
    }
    touchGrowAnim.current = requestAnimationFrame(shrink)
    return () => {
      if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    }
  }, [isTouching, isTouchHoleVisible])

  // 터치/마우스 이동
  const handleTouchMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isTouching) return
      if ('touches' in e) {
        if (e.touches.length !== 1) return
        const clientX = e.touches[0].clientX
        const clientY = e.touches[0].clientY
        setTouchPosition({ x: clientX, y: clientY })
      } else {
        const clientX = e.clientX
        const clientY = e.clientY
        setTouchPosition({ x: clientX, y: clientY })
      }
    },
    [isTouching],
  )

  // 터치/마우스 끝
  const handleTouchEnd = useCallback(() => {
    setIsTouching(false)
  }, [])

  // 정리
  useEffect(() => {
    return () => {
      if (autoAppearTimeout.current) clearTimeout(autoAppearTimeout.current)
      if (autoPulseAnim.current) cancelAnimationFrame(autoPulseAnim.current)
      if (touchGrowAnim.current) cancelAnimationFrame(touchGrowAnim.current)
    }
  }, [])

  // 자동 원 위치 계산
  const autoHolePosition =
    typeof window !== 'undefined'
      ? {
          x: window.innerWidth * HOLE_CONFIG.auto.position.x,
          y: window.innerHeight * HOLE_CONFIG.auto.position.y,
        }
      : null

  return (
    <div
      className="fixed inset-0 w-screen h-screen overflow-hidden select-none"
      style={{
        touchAction: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      onTouchStart={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleTouchStart(e)
      }}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleTouchStart(e)
      }}
      onMouseMove={handleTouchMove}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      onDragStart={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Background />
      <Overlay
        touchPosition={isTouchHoleVisible ? touchPosition : null}
        touchHoleSize={isTouchHoleVisible ? touchHoleSize : 0}
        autoHolePosition={isAutoHoleVisible ? autoHolePosition : null}
        autoHoleSize={isAutoHoleVisible ? autoHoleSize : 0}
      />
    </div>
  )
}

const Background = () => {
  const [isDesktop, setIsDesktop] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    // 윈도우 사이즈로 데스크탑 여부 판단
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])

  // webview auto replay 보장
  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.play()
    }
  }, [])

  return (
    <div className="fixed inset-0 w-full h-full">
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
      >
        <source
          src={
            isDesktop
              ? '/images/branding/glassis/teasing_vid_wide_4sec.mp4'
              : '/images/branding/glassis/teasing_vid_4sec.mp4'
          }
          type="video/mp4"
        />
      </video>
    </div>
  )
}

interface OverlayProps {
  touchPosition: TouchPosition | null
  touchHoleSize: number
  autoHolePosition: TouchPosition | null
  autoHoleSize: number
}

const Overlay = ({ touchPosition, touchHoleSize, autoHolePosition, autoHoleSize }: OverlayProps) => {
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [isCompleteOverlayVisible, setIsCompleteOverlayVisible] = useState(false)

  let mask = 'none'
  if (touchPosition && touchHoleSize > 0) {
    mask = `radial-gradient(circle ${touchHoleSize}px at ${touchPosition.x}px ${touchPosition.y}px, transparent 50%, black 50%)`
  } else if (autoHolePosition && autoHoleSize > 0) {
    mask = `radial-gradient(circle ${autoHoleSize}px at ${autoHolePosition.x}px ${autoHolePosition.y}px, transparent 50%, black 50%)`
  }
  const maskStyle = {
    WebkitMaskImage: mask,
    maskImage: mask,
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    amplitudeTracker.track('open_glassis_teaser_form')
    setIsFormVisible(true)
  }

  const isShowButton = isCurrentTimeInRange({
    to: GLASSIS_TEASER_END_TIME,
  })

  return (
    <>
      <div className="fixed inset-0 w-full h-full bg-black transition-opacity duration-300" style={maskStyle}>
        {autoHolePosition && autoHoleSize > 0 && (
          <FlexCol className="absolute top-[calc(30%-110px)] left-1/2 -translate-x-1/2 gap-[11px] items-center">
            <TouchHereSvg />
            <TouchHereArrowSvg />
          </FlexCol>
        )}

        <FlexCol className="w-full h-full flex items-center justify-center p-4 gap-[60px]">
          <div className="pt-[10%] w-full px-4 max-w-[356px]">
            <GlassisTeaserGlassesSvg />
          </div>
          <div className="flex flex-col items-center justify-center p-4 text-white gap-6">
            <TeasingTextSvg />
            {isShowButton && (
              <button className="flex items-center gap-2" onClick={handleClick}>
                <ButtonTextSvg /> <ArrowRightSvg />
              </button>
            )}
          </div>
        </FlexCol>
      </div>
      {isFormVisible && (
        <GlassisForm closeForm={() => setIsFormVisible(false)} onCloseForm={() => setIsCompleteOverlayVisible(true)} />
      )}
      {isCompleteOverlayVisible && (
        <div
          className="fixed inset-0 w-full h-full flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setIsCompleteOverlayVisible(false)}
        >
          <div
            className="flex flex-col items-center justify-center"
            style={{
              fontFamily: "'Noto Sans CJK KR', 'Noto Sans KR', sans-serif",
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '21px',
              letterSpacing: '8%',
              textAlign: 'center',
              color: 'white',
              userSelect: 'none',
            }}
          >
            <div>등록이 완료되었습니다.</div>
            <div>런칭 소식을 문자로 전해드려요.</div>
          </div>
        </div>
      )}
      <footer
        className={`
          fixed bottom-0 left-0 w-full z-50 h-8
          text-white text-center
          font-['Akzidenz-Grotesk_Next'] text-[10.5px] font-medium
          leading-[18px] tracking-[0.84px]
          bg-transparent
          px-0
          select-none
          pointer-events-none
        `}
      >
        ©Healing Paper Co.,Ltd. all rights reserved.
      </footer>
    </>
  )
}

export default GlassisTeaserPage
